import type { GA4Item, ListContext } from '~/gtm/types';
import type { ApiProductVariant, Product, ProductVariantDetails } from '~/types/Product';

// Union type for variant - supports both legacy and new API variants
type ProductVariant = ProductVariantDetails | ApiProductVariant | null;

/**
 * Convert price from cents to currency units
 */
function formatPrice(priceInCents: number | undefined): number | undefined {
  if (priceInCents === undefined) return undefined;
  return priceInCents / 100;
}

/**
 * Extract category hierarchy from product taxons or breadcrumb path
 */
function extractCategories(product: Product, breadcrumbPath?: { name: string; slug: string }[]): Partial<GA4Item> {
  const categories: Partial<GA4Item> = {};

  if (breadcrumbPath && breadcrumbPath.length > 0) {
    breadcrumbPath.forEach((crumb, index) => {
      if (index === 0) {
        categories.item_category = crumb.name;
      } else if (index === 1) {
        categories.item_category2 = crumb.name;
      } else if (index === 2) {
        categories.item_category3 = crumb.name;
      } else if (index === 3) {
        categories.item_category4 = crumb.name;
      } else if (index === 4) {
        categories.item_category5 = crumb.name;
      }
    });
  }

  return categories;
}

/**
 * Map a Product to GA4 item format
 * TODO: variant.price is currently WITH VAT - API should provide priceWithoutTax
 */
export function mapProductToGA4Item(
  product: Product,
  variant?: ProductVariant,
  listContext?: ListContext,
  index?: number,
  breadcrumbPath?: { name: string; slug: string }[]
): GA4Item {
  const selectedVariant = variant || product.defaultVariantData;
  const categories = extractCategories(product, breadcrumbPath);

  const item: GA4Item = {
    item_id: selectedVariant?.code || product.code,
    item_name: product.name,
    ...categories,
    // TODO: Should be price WITHOUT VAT (priceWithoutTax from API)
    price: formatPrice(selectedVariant?.price),
    quantity: 1,
  };

  // Add variant name if available
  if (selectedVariant?.name) {
    item.item_variant = selectedVariant.name;
  } else if (selectedVariant?.optionValues && selectedVariant.optionValues.length > 0) {
    item.item_variant = selectedVariant.optionValues.map((ov) => ov.value).join(' / ');
  }

  // Add list context if provided
  if (listContext?.item_list_id) {
    item.item_list_id = listContext.item_list_id;
  }
  if (listContext?.item_list_name) {
    item.item_list_name = listContext.item_list_name;
  }

  // Add index if provided (convert to 1-based for GA4)
  if (index !== undefined) {
    item.index = index + 1;
  }

  // Add discount if there's an original price
  // TODO: Should be discount WITHOUT VAT
  if (selectedVariant?.originalPrice && selectedVariant?.price) {
    const discount = selectedVariant.originalPrice - selectedVariant.price;
    if (discount > 0) {
      item.discount = formatPrice(discount);
    }
  }

  return item;
}

/**
 * Map multiple products to GA4 items array
 */
export function mapProductsToGA4Items(products: Product[], listContext?: ListContext): GA4Item[] {
  return products.map((product, index) => mapProductToGA4Item(product, null, listContext, index));
}
